import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requiredField, useForm } from '../../../../../utils/validators';

import { ModerationTransitionConditions } from '../../../../../models';

import { Button, WeezHeader, ToastContext, FrameCard } from '@weezevent/nacre';

import LoadingCard from '../../../../../components/loadingCard';
import { TransitionConditionSettings } from './handler/SettingsCard';
import TransitionConditionComponent from './handler/ConditionsHandler';
import { LANGUAGES } from '../../../../../components/translations/translationModal';

export default function ConditionDetails({ organization, flowId, conditionId, modal = false, setExternalDatas = () => {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            type: requiredField
        };
    }, []);

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            description: '',
            type: '',
            rates: [],
            delay: 3600,
            active: true,
            start_date: null,
            end_date: null,
            form_type: 'attendee',
            regex: '',
            source: null,
            score: null,
            translations: {
                name: {},
                description: {}
            }
        },
        validators
    );

    const [, loadingCondition, sync] = ModerationTransitionConditions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            id: conditionId,
            cache: false,
            allow_cache: false,
            launch: Boolean(conditionId),
            query: {
                expand: 'rates'
            },
            onSuccess: resp => {
                const trans = {
                    name: resp.object.translations.name || {},
                    description: resp.object.translations.description || {}
                };
                setFormObject({ ...form, ...resp.object, translations: trans });
            }
        },
        [organization, flowId, conditionId]
    );

    const languageOptions = React.useMemo(() => {
        return organization.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions]);

    const backLink = React.useMemo(() => `/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/conditions`, [organization.id, flowId]);

    const handleSubmit = React.useCallback(
        (redirect = false) => {
            let condition = new ModerationTransitionConditions({ ...form });

            condition.rates = [...form.rates.map(r => r.id)];

            if (!Object.keys(condition.translations['name'])) delete condition.translations['name'];
            if (!Object.keys(condition.translations['description'])) delete condition.translations['description'];

            return condition
                .save({ organizationId: organization.id, flowId })
                .then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.conditions.detail.messageSuccess${form.id ? 'Edit' : 'Save'}`));
                    if (redirect) {
                        history.push(backLink);
                    } else {
                        sync();
                    }
                })
                .catch(() => {
                    toast.error(t(`ticket.moderation.statusesTransitions.conditions.detail.messageError${form.id ? 'Edit' : 'Save'}`));
                });
        },
        [form.rates, backLink, organization.id, flowId, sync]
    );

    React.useEffect(() => {
        const isValid = formValidate();
        setExternalDatas({
            errors: !isValid,
            onSave: handleSubmit
        });
    }, [form]);

    return (
        <>
            {!modal && (
                <WeezHeader
                    title={!form.id ? t('ticket.moderation.statusesTransitions.conditions.detail.titleAdd') : form?.name}
                    rightComponent={[
                        <Button key={0} primary label={t('ticket.common.action.save')} disabled={!!Object.keys(formErrors).length} onClick={() => handleSubmit(true)} />
                    ]}
                    backLink={{
                        onClick: () => {
                            history.push(backLink);
                        }
                    }}
                />
            )}

            <FrameCard separator title={t('ticket.moderation.statusesTransitions.conditions.detail.settingsCardTitle')}>
                <LoadingCard loading={form.id && loadingCondition}>
                    <TransitionConditionSettings
                        condition={form}
                        handleCondition={setForm}
                        handleConditionObject={setFormObject}
                        languageOptions={languageOptions}
                        defaultLanguage={defaultLanguage}
                    />
                </LoadingCard>
            </FrameCard>

            {form.type && (
                <FrameCard
                    separator
                    title={`${t('ticket.moderation.statusesTransitions.conditions.cardTitle')}: ${t(
                        `ticket.moderation.statusesTransitions.transitionConditionsList.conditionTypes.${form.type}`
                    )}`}
                >
                    <LoadingCard loading={form.id && loadingCondition}>
                        <TransitionConditionComponent
                            organizationId={organization.pk}
                            flowId={flowId}
                            condition={form}
                            handleCondition={setForm}
                            handleConditionObject={setFormObject}
                        />
                    </LoadingCard>
                </FrameCard>
            )}
        </>
    );
}
